import React, { useEffect, useRef, useState, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import CreateInvoiceModal from "./CreateInvoiceModal";
import RefreshContext from "./RefreshContext";
import apiurl from "../Api";

const normalizePostValue = (value) =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const normalizeDecisionValue = (decision) =>
  (decision || "").toString().trim().toLowerCase();

const DEFAULT_STATUS_CONFIG = Object.freeze({
  defaultStatus: { label: "Processing", cls: "bg-warning" },
  statusMap: {
    validated: { label: "Process for Settlement", cls: "bg-info" },
    approved: { label: "Invoice Settled", cls: "bg-success" },
    payment: { label: "Payment", cls: "bg-primary" },
    paid: { label: "Paid", cls: "bg-success" },
    rejected: { label: "Rejected", cls: "bg-danger" },
    reject2: { label: "Rejected", cls: "bg-danger" },
  },
  rejectedDecisions: ["rejected", "reject2"],
});

const normalizeStatusConfig = (rawConfig) => {
  const normalized = {
    defaultStatus: { ...DEFAULT_STATUS_CONFIG.defaultStatus },
    statusMap: { ...DEFAULT_STATUS_CONFIG.statusMap },
    rejectedDecisions: [...DEFAULT_STATUS_CONFIG.rejectedDecisions],
  };

  if (!rawConfig || typeof rawConfig !== "object") return normalized;

  const defaultLabel = (rawConfig?.default?.label || "").toString().trim();
  const defaultClass = (rawConfig?.default?.badge_class || rawConfig?.default?.cls || "")
    .toString()
    .trim();
  if (defaultLabel) normalized.defaultStatus.label = defaultLabel;
  if (defaultClass) normalized.defaultStatus.cls = defaultClass;

  if (rawConfig.statuses && typeof rawConfig.statuses === "object") {
    const dynamicMap = {};
    Object.entries(rawConfig.statuses).forEach(([decision, meta]) => {
      const key = normalizeDecisionValue(decision);
      if (!key || !meta || typeof meta !== "object") return;
      const label = (meta.label || "").toString().trim() || key;
      const cls = (meta.badge_class || meta.cls || "").toString().trim() || normalized.defaultStatus.cls;
      dynamicMap[key] = { label, cls };
    });
    if (Object.keys(dynamicMap).length > 0) {
      normalized.statusMap = dynamicMap;
    }
  }

  if (Array.isArray(rawConfig.rejected_decisions)) {
    const rejectedList = rawConfig.rejected_decisions
      .map((item) => normalizeDecisionValue(item))
      .filter(Boolean);
    if (rejectedList.length > 0) {
      normalized.rejectedDecisions = rejectedList;
    }
  }

  return normalized;
};

function VendorPayment() {
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationInput, setValidationInput] = useState("");
  const [editingValidationId, setEditingValidationId] = useState(null);
  const [validationParams, setValidationParams] = useState([]);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationFilter, setValidationFilter] = useState("all");
  const [categoryParams, setCategoryParams] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryRatio, setCategoryRatio] = useState("");
  const [categoryPostfix, setCategoryPostfix] = useState("");
  const [categoryIsActive, setCategoryIsActive] = useState("1");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [newVendorName, setNewVendorName] = useState("");
  const [newVendorLocation, setNewVendorLocation] = useState("");
  const [addVendorLoading, setAddVendorLoading] = useState(false);
  const [userPost, setUserPost] = useState("");
  const [statusConfig, setStatusConfig] = useState(DEFAULT_STATUS_CONFIG);
  const [tdsDraftByInvoice, setTdsDraftByInvoice] = useState({});
  const [tdsAppliedByInvoice, setTdsAppliedByInvoice] = useState({});
  const invoicePrintRef = useRef(null);
  const { refreshKey } = useContext(RefreshContext);
  const categoryApiUrl = `${apiurl}categoryadd.php`;
  const categoryStatusOptions = [
    { value: "1", label: "Active" },
    { value: "0", label: "Inactive" },
  ];

  const isBusinessAssociate = userPost === "business_associate";
  const isAccountsUser = ["accounts", "accounts_head"].includes(userPost);
  const isBusinessManager = userPost === "business_manager";
  const effectiveStatusFilter = isAccountsUser ? "completed" : "all";
  const showRejectionReasonColumn =
    (isBusinessAssociate || isBusinessManager) &&
    effectiveStatusFilter !== "completed";
  const showReviewColumn =
    !isBusinessAssociate && !(isBusinessManager && effectiveStatusFilter === "completed");
  const showBAFinancialColumns = isBusinessAssociate;
  const showBMFinancialColumns = isBusinessManager;
  const showAccountsFinancialColumns = isAccountsUser;
  const showSharingRatioColumn = showBAFinancialColumns || showBMFinancialColumns;
  const showInvoiceAmountColumn = !showBMFinancialColumns && !showAccountsFinancialColumns;
  const showPayableAmountColumn =
    showBAFinancialColumns || showBMFinancialColumns || showAccountsFinancialColumns;
  const showBMNameColumn = showBAFinancialColumns;
  const showBANameColumn = showBMFinancialColumns;
  const showBankDetailsColumn = showBMFinancialColumns;
  const showTdsColumn = showAccountsFinancialColumns;
  const showNetPayableColumn = showAccountsFinancialColumns;
  const tableColSpan =
    7 +
    (showSharingRatioColumn ? 1 : 0) +
    (showInvoiceAmountColumn ? 1 : 0) +
    (showPayableAmountColumn ? 1 : 0) +
    (showBMNameColumn ? 1 : 0) +
    (showBANameColumn ? 1 : 0) +
    (showBankDetailsColumn ? 1 : 0) +
    (showTdsColumn ? 1 : 0) +
    (showNetPayableColumn ? 1 : 0) +
    (showRejectionReasonColumn ? 1 : 0) +
    (showReviewColumn ? 1 : 0);

  const fetchSessionUser = async () => {
    try {
      const response = await axios.post(`${apiurl}session.php`);
      const post = normalizePostValue(response.data?.post || "");
      setUserPost(post);
    } catch (error) {
      setUserPost("");
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiurl}save_invoice.php`);
      if (response.data.status === true) {
        setInvoices(response.data.data || []);
        setStatusConfig(normalizeStatusConfig(response.data?.status_config));
      } else {
        setInvoices([]);
        setStatusConfig(DEFAULT_STATUS_CONFIG);
      }
    } catch (error) {
      setInvoices([]);
      setStatusConfig(DEFAULT_STATUS_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Array.isArray(invoices) || invoices.length === 0) return;
    const draftSeed = {};
    const appliedSeed = {};
    invoices.forEach((item) => {
      const invoiceId = Number(item?.id);
      if (!Number.isFinite(invoiceId)) return;
      const tdsFromRow = parseAmountNumber(item?.tds_percentage);
      if (Number.isFinite(tdsFromRow)) {
        draftSeed[invoiceId] = String(tdsFromRow);
        appliedSeed[invoiceId] = tdsFromRow;
      }
    });
    if (Object.keys(draftSeed).length > 0) {
      setTdsDraftByInvoice((prev) => ({ ...draftSeed, ...prev }));
    }
    if (Object.keys(appliedSeed).length > 0) {
      setTdsAppliedByInvoice((prev) => ({ ...appliedSeed, ...prev }));
    }
  }, [invoices]);

  useEffect(() => {
    fetchSessionUser();
    fetchInvoices();
  }, [refreshKey]);

  const formatAmount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return `INR ${num.toLocaleString("en-IN")}`;
  };

  const parseAmountNumber = (value) => {
    const cleaned = (value || "").toString().replace(/[^0-9.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : NaN;
  };

  const getSharingPercent = (invoice) => {
    const raw = (invoice?.ba_sharing_ratio || "").toString().trim().toLowerCase();
    if (!raw) return NaN;
    const slugMatch = raw.match(/^(\d+)_([0-9]{1,2})(?:_|$)/);
    if (slugMatch) {
      return Number(`${slugMatch[1]}.${slugMatch[2]}`);
    }
    const numericMatch = raw.match(/(\d+(?:\.\d+)?)/);
    if (!numericMatch) return NaN;
    return Number(numericMatch[1]);
  };

  const formatSharingRatio = (invoice) => {
    const ratio = getSharingPercent(invoice);
    if (Number.isFinite(ratio)) return `${ratio.toFixed(2)}%`;
    return "-";
  };

  const getPayableAmount = (invoice) => {
    const invoiceAmount = parseAmountNumber(invoice?.invoice_amount);
    const sharingPercent = getSharingPercent(invoice);
    if (!Number.isFinite(invoiceAmount)) return null;
    if (!Number.isFinite(sharingPercent)) return invoiceAmount;
    const payable = (invoiceAmount * sharingPercent) / 100;
    return payable >= 0 ? payable : 0;
  };

  const handleTdsInputChange = (invoiceId, value) => {
    const cleaned = (value || "").toString().replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    const normalized =
      parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}` : parts[0];
    setTdsDraftByInvoice((prev) => ({ ...prev, [invoiceId]: normalized }));
  };

  const applyTdsForInvoice = async (invoice) => {
    const invoiceId = Number(invoice?.id);
    const raw = (tdsDraftByInvoice[invoiceId] || "").toString().trim();
    if (!raw) {
      Swal.fire("Warning", "Please enter TDS percentage", "warning");
      return;
    }
    const tds = Number(raw);
    if (!Number.isFinite(tds) || tds <= 0 || tds > 100) {
      Swal.fire("Warning", "TDS percentage should be greater than 0 and up to 100", "warning");
      return;
    }
    const payable = getPayableAmount(invoice);
    if (!Number.isFinite(payable)) {
      Swal.fire("Warning", "Payable amount is not available", "warning");
      return;
    }
    const netPayable = payable - (payable * tds) / 100;
    if (!Number.isFinite(netPayable) || netPayable < 0) {
      Swal.fire("Warning", "Unable to calculate net payable amount", "warning");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("action", "update_tds");
      formData.append("id", String(invoiceId));
      formData.append("tds_percentage", String(tds));
      formData.append("net_payable_amount", String(netPayable));
      const response = await axios.post(`${apiurl}save_invoice.php`, formData);
      if (response.data.status === true) {
        setTdsAppliedByInvoice((prev) => ({ ...prev, [invoiceId]: tds }));
        setInvoices((prev) =>
          prev.map((row) =>
            Number(row.id) === invoiceId
              ? {
                  ...row,
                  tds_percentage: response.data?.data?.tds_percentage ?? String(tds),
                  net_payable_amount: response.data?.data?.net_payable_amount ?? String(netPayable),
                  decision: response.data?.data?.decision ?? row.decision,
                }
              : row
          )
        );
        Swal.fire({
          position: "top-center",
          icon: response.data?.data?.status_changed ? "success" : "info",
          title: response.data?.message || "TDS updated",
          text: response.data?.data?.status_label
            ? `Current Status: ${response.data.data.status_label}`
            : "",
          showConfirmButton: false,
          timer: 2200,
        });
      } else {
        Swal.fire("Error", response.data.message || "Unable to save TDS", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const getNetPayableAmount = (invoice) => {
    const invoiceId = Number(invoice?.id);
    const payable = getPayableAmount(invoice);
    const storedNet = parseAmountNumber(invoice?.net_payable_amount);
    if (Number.isFinite(storedNet)) return storedNet;
    const tds = tdsAppliedByInvoice[invoiceId];
    if (!Number.isFinite(payable) || !Number.isFinite(tds)) return null;
    const net = payable - (payable * tds) / 100;
    return net >= 0 ? net : 0;
  };

  const formatBankDetails = (invoice) => {
    const account = (invoice?.ba_bank_account_no || "").toString().trim();
    const ifsc = (invoice?.ba_ifsc_code || "").toString().trim();
    if (!account && !ifsc) return "-";
    if (account && ifsc) return `A/C: ${account} | IFSC: ${ifsc}`;
    if (account) return `A/C: ${account}`;
    return `IFSC: ${ifsc}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-GB");
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const getServicePeriodParts = (invoice) => {
    const fromDate = (invoice?.service_from_date || "").toString().trim();
    const toDate = (invoice?.service_to_date || "").toString().trim();
    if (fromDate && toDate) {
      return {
        hasRange: true,
        from: formatDate(fromDate),
        to: formatDate(toDate),
        legacy: "",
      };
    }
    const legacy = (invoice?.service_period || "").toString().trim();
    const matches = legacy.match(/\d{4}-\d{2}-\d{2}/g);
    if (matches && matches.length >= 2) {
      return {
        hasRange: true,
        from: formatDate(matches[0]),
        to: formatDate(matches[1]),
        legacy: "",
      };
    }
    return {
      hasRange: false,
      from: "",
      to: "",
      legacy: legacy || "-",
    };
  };

  const normalizeDecision = (decision) => normalizeDecisionValue(decision);

  const getStatus = (invoice) => {
    const normalized = normalizeDecision(invoice?.decision);
    return statusConfig.statusMap[normalized] || statusConfig.defaultStatus;
  };

  const isRejectedDecision = (decision) => {
    const normalized = normalizeDecision(decision);
    return statusConfig.rejectedDecisions.includes(normalized);
  };
  const isPaidDecision = (decision) => normalizeDecision(decision) === "paid";

  const handleDelete = async (invoice) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This invoice will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", invoice.id);

      const response = await axios.post(`${apiurl}save_invoice.php`, formData);

      if (response.data.status === true) {
        Swal.fire("Deleted!", response.data.message, "success");
        fetchInvoices();
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const openCreateModal = () => {
    setEditingInvoice(null);
    setShowModal(true);
  };

  const openEditModal = (invoice) => {
    if (isAccountsUser) {
      const tds = parseAmountNumber(invoice?.tds_percentage);
      const net = parseAmountNumber(invoice?.net_payable_amount);
      if (!Number.isFinite(tds) || tds <= 0 || !Number.isFinite(net)) {
        Swal.fire("Warning", "Submit TDS percentage first", "warning");
        return;
      }
    }
    setEditingInvoice(invoice);
    setShowModal(true);
  };

  const closeInvoiceModal = () => {
    setShowModal(false);
    setEditingInvoice(null);
  };

  const openViewModal = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedInvoice(null);
  };

  const openPaymentDetailsModal = (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentDetailsModal(true);
  };

  const closePaymentDetailsModal = () => {
    setShowPaymentDetailsModal(false);
    setSelectedPaymentInvoice(null);
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const openPaymentReceiptDocument = (fileName) => {
    const safeName = (fileName || "").toString().trim();
    if (!safeName) return;
    const safeUrl = `${apiurl}uploads/receipts/${encodeURIComponent(safeName)}`;
    window.open(safeUrl, "_blank", "noopener,noreferrer");
  };

  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const fetchValidationParams = async (filter = "all") => {
    try {
      setValidationLoading(true);
      const query = filter === "all" ? "" : "?active=1";
      const response = await axios.get(`${apiurl}validation_parameters.php${query}`);
      if (response.data.status === true) {
        setValidationParams(response.data.data || []);
      } else {
        setValidationParams([]);
      }
    } catch (error) {
      setValidationParams([]);
    } finally {
      setValidationLoading(false);
    }
  };

  useEffect(() => {
    fetchValidationParams(validationFilter);
  }, [refreshKey]);

  const categoryList = selectedInvoice ? parseArray(selectedInvoice.category) : [];
  const paymentCategoryList = selectedPaymentInvoice ? parseArray(selectedPaymentInvoice.category) : [];

  const openValidationModal = () => {
    if (!isBusinessAssociate) return;
    setShowValidationModal(true);
    setValidationInput("");
    setEditingValidationId(null);
    fetchValidationParams(validationFilter);
  };

  const closeValidationModal = () => {
    setShowValidationModal(false);
    setValidationInput("");
    setEditingValidationId(null);
  };

  const handleValidationSave = async () => {
    const trimmed = validationInput.trim();
    if (!trimmed) return;

    try {
      const formData = new FormData();
      if (editingValidationId !== null) {
        formData.append("action", "update");
        formData.append("id", editingValidationId);
      } else {
        formData.append("action", "create");
      }
      formData.append("parameter", trimmed);
      formData.append("is_active", "1");

      const response = await axios.post(`${apiurl}validation_parameters.php`, formData);

      if (response.data.status === true) {
        setValidationInput("");
        setEditingValidationId(null);
        fetchValidationParams(validationFilter);
        Swal.fire("Success", response.data.message, "success");
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const handleValidationEdit = (item) => {
    setEditingValidationId(item.id);
    setValidationInput(item.parameter || "");
  };

  const handleValidationDelete = async (item) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This parameter will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", item.id);

      const response = await axios.post(`${apiurl}validation_parameters.php`, formData);

      if (response.data.status === true) {
        Swal.fire("Deleted!", response.data.message, "success");
        fetchValidationParams(validationFilter);
        if (editingValidationId === item.id) {
          setEditingValidationId(null);
          setValidationInput("");
        }
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const handleValidationToggle = async (item) => {
    try {
      const formData = new FormData();
      formData.append("action", "toggle");
      formData.append("id", item.id);
      formData.append("is_active", Number(item.is_active) === 1 ? "0" : "1");

      const response = await axios.post(`${apiurl}validation_parameters.php`, formData);

      if (response.data.status === true) {
        fetchValidationParams(validationFilter);
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const handleValidationFilterChange = (value) => {
    setValidationFilter(value);
    fetchValidationParams(value);
  };

  const fetchCategoryParams = async () => {
    try {
      setCategoryLoading(true);
      const response = await axios.get(`${categoryApiUrl}?category_parameters=1`);
      if (response.data.status === true) {
        setCategoryParams(response.data.data || []);
      } else {
        setCategoryParams([]);
      }
    } catch (error) {
      setCategoryParams([]);
    } finally {
      setCategoryLoading(false);
    }
  };

  const openCategoryModal = () => {
    if (!isBusinessAssociate) return;
    setShowCategoryModal(true);
    setEditingCategoryId(null);
    setCategoryName("");
    setCategoryDescription("");
    setCategoryRatio("");
    setCategoryPostfix("");
    setCategoryIsActive("1");
    fetchCategoryParams();
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setCategoryName("");
    setCategoryDescription("");
    setCategoryRatio("");
    setCategoryPostfix("");
    setCategoryIsActive("1");
    setEditingCategoryId(null);
  };

  const openAddVendorModal = () => {
    setShowAddVendorModal(true);
    setNewVendorName("");
    setNewVendorLocation("");
  };

  const closeAddVendorModal = () => {
    setShowAddVendorModal(false);
    setNewVendorName("");
    setNewVendorLocation("");
  };

  const handleAddVendorUiSubmit = async () => {
    const vendorName = newVendorName.trim();
    const location = newVendorLocation.trim();

    if (!vendorName || !location) {
      Swal.fire("Please fill vendor name and location", "", "warning");
      return;
    }

    try {
      setAddVendorLoading(true);
      const formData = new FormData();
      formData.append("action", "add_external_vendor");
      formData.append("vendor_name", vendorName);
      formData.append("location", location);

      const response = await axios.post(`${apiurl}save_invoice.php`, formData);
      if (response.data.status === true) {
        Swal.fire("Success", response.data.message || "Vendor added successfully", "success");
        closeAddVendorModal();
      } else {
        Swal.fire("Error", response.data.message || "Unable to add vendor", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    } finally {
      setAddVendorLoading(false);
    }
  };

  const handleCategorySave = async () => {
    if (!categoryName.trim() || !categoryRatio.toString().trim()) {
      Swal.fire("Please fill Category and Sharing Ratio", "", "warning");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("category_action", editingCategoryId ? "update" : "create");
      if (editingCategoryId) {
        formData.append("id", editingCategoryId);
      }
      formData.append("category", categoryName.trim());
      formData.append("description", categoryDescription.trim());
      formData.append("sharing_ratio", categoryRatio);
      formData.append("sharing_postfix", categoryPostfix.trim());
      formData.append("is_active", categoryIsActive);
      const response = await axios.post(categoryApiUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status === true) {
        setCategoryName("");
        setCategoryDescription("");
        setCategoryRatio("");
        setCategoryPostfix("");
        setCategoryIsActive("1");
        setEditingCategoryId(null);
        fetchCategoryParams();
        Swal.fire(editingCategoryId ? "Category Updated" : "Category Added", "", "success");
      } else {
        Swal.fire(response.data.message || "Error", "", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const handleCategoryEdit = (item) => {
    setEditingCategoryId(String(item.id));
    setCategoryName(item.category || "");
    setCategoryDescription(item.description || "");
    setCategoryRatio(item.sharing_ratio ?? "");
    setCategoryPostfix(item.sharing_postfix || "");
    setCategoryIsActive(Number(item.is_active) === 1 ? "1" : "0");
  };

  const handleCategoryToggle = async (item) => {
    try {
      const formData = new FormData();
      formData.append("category_action", "toggle");
      formData.append("id", item.id);
      formData.append("is_active", Number(item.is_active) === 1 ? "0" : "1");
      const response = await axios.post(categoryApiUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status === true) {
        fetchCategoryParams();
      } else {
        Swal.fire(response.data.message || "Error", "", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!id) return;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This category will be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      const formData = new FormData();
      formData.append("category_action", "delete");
      formData.append("id", id);
      const response = await axios.post(categoryApiUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status === true) {
        fetchCategoryParams();
        Swal.fire("Deleted", "", "success");
      } else {
        Swal.fire(response.data.message || "Error", "", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server Error", "error");
    }
  };

  const filteredInvoices = invoices.filter((item) => {
    if (isAccountsUser) {
      const normalized = normalizeDecision(item?.decision);
      return normalized === "validated" || normalized === "approved";
    }
    return true;
  });

  return (
    <>
      <style>{`
        .table-head-sky th {
          background: transparent !important;
          color: #000000 !important;
        }
        .card-head-sky {
          background: #87cefa !important;
          color: #ffffff !important;
        }
        .invoice-shell {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 28px;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
        }
        .invoice-header {
          border-bottom: 2px solid #111827;
          padding-bottom: 16px;
          margin-bottom: 18px;
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: start;
          gap: 16px;
        }
        .invoice-header-right {
          justify-self: end;
        }
        .invoice-title {
          font-size: 26px;
          letter-spacing: 0.12em;
        }
        .invoice-subtitle {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: #6b7280;
        }
        .invoice-meta-card {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px 14px;
          background: #f8fafc;
          min-width: 220px;
        }
        .invoice-meta-row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          font-size: 13px;
          color: #334155;
        }
        .invoice-meta-row span:first-child {
          color: #64748b;
        }
        .invoice-billto {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 14px;
        }
        .invoice-table th {
          background: transparent !important;
          color: #000000 !important;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 0.06em;
        }
        .invoice-footer-note {
          border-top: 1px solid #e5e7eb;
          padding-top: 12px;
          margin-top: 16px;
          font-size: 12px;
          color: #6b7280;
        }
        .invoice-total-card {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px 16px;
          background: #f9fafb;
        }
        .invoice-total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
          font-size: 14px;
          color: #334155;
        }
        .invoice-total-row.total {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin-top: 10px;
        }
        .vendor-payment-scroll {
          display: flex;
          flex: 1 1 auto;
          min-width: 0;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          width: 100%;
          max-width: 100%;
          -webkit-overflow-scrolling: touch;
          background: #ffffff;
        }
        .vendor-payment-card {
          display: flex;
          flex-direction: column;
          min-width: 0;
          box-shadow: none !important;
          border: 1px solid #dbe2ea;
          overflow: hidden !important;
          background: #ffffff;
        }
        .vendor-payment-card .card-body {
          min-width: 0;
          background: #ffffff;
          overflow: hidden !important;
        }
        .vendor-payment-table {
          flex: 0 0 auto;
          min-width: 1120px;
          width: 100%;
          max-width: none;
        }
        .vendor-payment-table th {
          white-space: nowrap;
        }
        .vendor-payment-table td {
          vertical-align: middle;
        }
        .vendor-payment-table .reason-cell {
          white-space: normal;
          min-width: 200px;
          max-width: 260px;
          word-break: break-word;
        }
        .vendor-payment-table .status-badge {
          min-width: 110px;
          display: inline-flex;
          justify-content: center;
        }
        .vendor-payment-table .action-cell {
          min-width: 200px;
        }
        .vendor-payment-table .tds-cell {
          min-width: 170px;
        }
        .vendor-payment-table .tds-input-wrap {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .vendor-payment-table .tds-input {
          width: 70px;
          min-width: 70px;
          text-align: right;
          padding: 0.25rem 0.4rem;
        }
        .vendor-payment-table .tds-percent {
          font-weight: 600;
          color: #334155;
          min-width: 14px;
          text-align: center;
        }
        .vendor-payment-table .tds-apply-btn {
          min-width: 34px !important;
          padding: 0.25rem 0.45rem;
          line-height: 1;
        }
        .vendor-payment-table .action-cell .d-flex {
          flex-wrap: nowrap !important;
        }
        .vendor-payment-table .action-cell .btn {
          min-width: 82px;
          white-space: nowrap;
          padding-inline: 10px;
        }
        @media (max-width: 1200px) {
          .vendor-payment-table {
            min-width: 1040px;
          }
          .vendor-payment-table th,
          .vendor-payment-table td {
            font-size: 13px;
          }
        }
        @media print {
          @page {
            margin: 12mm;
          }
          html, body {
            height: auto !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .modal, .modal-dialog, .modal-content, .modal-body {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
          }
          .modal-backdrop {
            display: none !important;
          }
          body * {
            visibility: hidden;
          }
          #invoice-print, #invoice-print * {
            visibility: visible;
          }
          #invoice-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            display: block !important;
          }
          .no-print {
            display: none !important;
          }
          .invoice-two-col {
            display: flex !important;
            flex-wrap: nowrap !important;
            gap: 12px !important;
          }
          .invoice-two-col > div {
            float: none !important;
            width: calc(50% - 6px) !important;
            flex: 0 0 calc(50% - 6px) !important;
            max-width: calc(50% - 6px) !important;
          }
          .invoice-two-col::before,
          .invoice-two-col::after {
            display: none !important;
            content: none !important;
          }
          .invoice-header {
            display: grid !important;
            grid-template-columns: 1fr auto !important;
            align-items: start !important;
            gap: 16px !important;
          }
          .invoice-header-right {
            justify-self: end !important;
            margin-left: auto !important;
          }
        }
      `}</style>
      <div className="main-content app-content">
        <div className="container-fluid mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="fw-bold mb-0 text-2xl">Vendor Payment</h2>
            {isBusinessAssociate ? (
              <div className="d-flex gap-2">
                <button className="btn btn-outline-primary" onClick={openValidationModal}>
                  Manage Validation
                </button>
                <button className="btn btn-outline-primary" onClick={openAddVendorModal}>
                  Add Vendor
                </button>
                <button className="btn btn-primary" onClick={openCreateModal}>
                  + Create Invoice
                </button>
              </div>
            ) : null}
          </div>

          <div className="card custom-card vendor-payment-card">
            <div className="card-body p-0">
              <div className="table-responsive vendor-payment-scroll">
                <table className="table table-bordered mb-0 align-middle vendor-payment-table">
                  <thead className="table-head-sky">
                    <tr>
                      <th style={{ width: "60px" }}>S.No</th>
                      <th>Invoice No</th>
                      <th>Client Name</th>
                      {showBANameColumn ? <th style={{ minWidth: "130px" }}>BA Name</th> : null}
                      {isBusinessAssociate ? (
                        <>
                          {showInvoiceAmountColumn ? <th>{showBAFinancialColumns ? "Invoice Amount" : "Amount"}</th> : null}
                          {showSharingRatioColumn ? <th style={{ minWidth: "120px" }}>Sharing Ratio</th> : null}
                          {showPayableAmountColumn ? <th style={{ minWidth: "130px" }}>Payable Amount</th> : null}
                        </>
                      ) : (
                        <>
                          {showSharingRatioColumn ? <th style={{ minWidth: "120px" }}>Sharing Ratio</th> : null}
                          {showInvoiceAmountColumn ? <th>{showBAFinancialColumns ? "Invoice Amount" : "Amount"}</th> : null}
                          {showPayableAmountColumn ? <th style={{ minWidth: "130px" }}>Payable Amount</th> : null}
                        </>
                      )}
                      {showBMNameColumn ? <th style={{ minWidth: "130px" }}>BM Name</th> : null}
                      {showBankDetailsColumn ? <th style={{ minWidth: "230px" }}>Bank Details</th> : null}
                      {showTdsColumn ? <th style={{ minWidth: "170px" }}>TDS Percentage</th> : null}
                      {showNetPayableColumn ? <th style={{ minWidth: "150px" }}>Net Payable Amount</th> : null}
                      <th>Date</th>
                      <th>Time</th>
                      <th className="text-center">Status</th>
                      {showRejectionReasonColumn ? <th style={{ width: "260px" }}>Rejection Reason</th> : null}
                      {showReviewColumn ? <th className="text-center" style={{ width: "140px" }}>Review</th> : null}
                      <th className="text-center" style={{ width: "220px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={tableColSpan} className="text-center text-muted py-4">
                          Loading...
                        </td>
                      </tr>
                    ) : filteredInvoices.length > 0 ? (
                      filteredInvoices.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold">{item.invoice_no}</td>
                          <td>{item.client_name}</td>
                          {showBANameColumn ? <td>{item.ba_name || "-"}</td> : null}
                          {isBusinessAssociate ? (
                            <>
                              {showInvoiceAmountColumn ? <td>{formatAmount(item.invoice_amount)}</td> : null}
                              {showSharingRatioColumn ? <td>{formatSharingRatio(item)}</td> : null}
                              {showPayableAmountColumn ? (
                                <td>
                                  {(() => {
                                    const payable = getPayableAmount(item);
                                    return payable === null ? "-" : formatAmount(payable);
                                  })()}
                                </td>
                              ) : null}
                            </>
                          ) : (
                            <>
                              {showSharingRatioColumn ? <td>{formatSharingRatio(item)}</td> : null}
                              {showInvoiceAmountColumn ? <td>{formatAmount(item.invoice_amount)}</td> : null}
                              {showPayableAmountColumn ? (
                                <td>
                                  {(() => {
                                    const payable = getPayableAmount(item);
                                    return payable === null ? "-" : formatAmount(payable);
                                  })()}
                                </td>
                              ) : null}
                            </>
                          )}
                          {showBMNameColumn ? <td>{item.manager_name || "-"}</td> : null}
                          {showBankDetailsColumn ? <td>{formatBankDetails(item)}</td> : null}
                          {showTdsColumn ? (
                            <td className="tds-cell">
                              <div className="tds-input-wrap">
                                <input
                                  type="text"
                                  className="form-control form-control-sm tds-input"
                                  placeholder="0"
                                  value={tdsDraftByInvoice[Number(item.id)] || ""}
                                  onChange={(e) =>
                                    handleTdsInputChange(Number(item.id), e.target.value)
                                  }
                                />
                                <span className="tds-percent">%</span>
                                <button
                                  className="btn btn-sm btn-outline-success tds-apply-btn"
                                  onClick={() => applyTdsForInvoice(item)}
                                  title="Apply TDS"
                                  disabled={
                                    !Number.isFinite(
                                      Number((tdsDraftByInvoice[Number(item.id)] || "").toString().trim())
                                    ) ||
                                    Number((tdsDraftByInvoice[Number(item.id)] || "").toString().trim()) <= 0
                                  }
                                >
                                  <i className="ri-check-line"></i>
                                </button>
                              </div>
                            </td>
                          ) : null}
                          {showNetPayableColumn ? (
                            <td>
                              {(() => {
                                const net = getNetPayableAmount(item);
                                return net === null ? "-" : formatAmount(net);
                              })()}
                            </td>
                          ) : null}
                          <td>{formatDate(item.invoice_date)}</td>
                          <td>{formatTime(item.created_at)}</td>
                          <td className="text-center">
                            {(() => {
                              const status = getStatus(item);
                              return (
                                <span className={`badge ${status.cls} px-3 py-2 status-badge`}>
                                  {status.label}
                                </span>
                              );
                            })()}
                          </td>
                          {showRejectionReasonColumn ? (
                            <td className="reason-cell">
                              {isRejectedDecision(item.decision) ? (
                                <span className="text-danger fw-semibold">
                                  {item.rejection_reason || "Rejected (Reason not provided)"}
                                </span>
                              ) : (
                                <span className="text-muted">NA</span>
                              )}
                            </td>
                          ) : null}
                          {showReviewColumn ? (
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => openEditModal(item)}
                              >
                                Check Invoice
                              </button>
                            </td>
                          ) : null}
                          <td className="text-center action-cell">
                            <div className="d-flex gap-2 justify-content-center flex-wrap">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => openViewModal(item)}
                              >
                                View
                              </button>
                              {isBusinessManager && isPaidDecision(item.decision) ? (
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => openPaymentDetailsModal(item)}
                                >
                                  View Payment Details
                                </button>
                              ) : null}
                              {isBusinessAssociate && isRejectedDecision(item.decision) ? (
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => openEditModal(item)}
                                >
                                  Check Invoice
                                </button>
                              ) : null}
                              {!isBusinessAssociate ? (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(item)}
                                >
                                  Cancel
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={tableColSpan} className="text-center text-muted py-4">
                          No invoices found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showViewModal && selectedInvoice ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-semibold mb-1">View Invoice</h5>
                    <div className="text-muted small">
                      Invoice No: <span className="fw-semibold">{selectedInvoice.invoice_no || "-"}</span>
                    </div>
                  </div>
                  <button className="btn-close" onClick={closeViewModal}></button>
                </div>

                <div className="modal-body">
                  <div id="invoice-print" ref={invoicePrintRef} className="invoice-shell">
                    <div className="invoice-header">
                      <div className="invoice-header-left">
                        <div className="invoice-title fw-bold">INVOICE</div>
                        <div className="invoice-subtitle">Official Invoice</div>
                        <div className="text-muted small mt-2">
                          This invoice is issued by the Business Associate for services rendered.
                        </div>
                      </div>
                      <div className="invoice-meta-card invoice-header-right">
                        <div className="invoice-meta-row">
                          <span>Invoice No</span>
                          <span className="fw-semibold">{selectedInvoice.invoice_no || "-"}</span>
                        </div>
                        <div className="invoice-meta-row">
                          <span>Invoice Date</span>
                          <span className="fw-semibold">{formatDate(selectedInvoice.invoice_date)}</span>
                        </div>
                        <div className="invoice-meta-row">
                          <span>Status</span>
                          <span className="fw-semibold">{getStatus(selectedInvoice).label}</span>
                        </div>
                        {isRejectedDecision(selectedInvoice.decision) ? (
                          <div className="invoice-meta-row">
                            <span className="text-danger fw-semibold">Reason</span>
                            <span className="text-danger fw-semibold">
                              {selectedInvoice.rejection_reason || "Rejected"}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="row g-3 mb-3 invoice-two-col">
                      <div className="col-md-6">
                        <div className="invoice-billto h-100">
                          <div className="fw-semibold mb-2">From (BA)</div>
                          <div className="text-muted small">BA Name</div>
                          <div className="fw-semibold">{selectedInvoice.ba_name || "-"}</div>
                          <div className="text-muted small mt-2">BA Code</div>
                          <div className="fw-semibold">{selectedInvoice.ba_code || "-"}</div>
                          <div className="text-muted small mt-2">GSTIN</div>
                          <div className="fw-semibold">{selectedInvoice.gstin || "-"}</div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="invoice-billto h-100">
                          <div className="fw-semibold mb-2">Bill To (Client)</div>
                          <div className="text-muted small">Client Name</div>
                          <div className="fw-semibold">{selectedInvoice.client_name || "-"}</div>
                          <div className="text-muted small mt-2">Client Code</div>
                          <div className="fw-semibold">{selectedInvoice.client_code || "-"}</div>
                          <div className="text-muted small mt-2">Location</div>
                          <div className="fw-semibold">{selectedInvoice.location || "-"}</div>
                          <div className="text-muted small mt-2">Service Period</div>
                          {(() => {
                            const servicePeriod = getServicePeriodParts(selectedInvoice);
                            if (servicePeriod.hasRange) {
                              return (
                                <>
                                  <div className="fw-semibold">From: {servicePeriod.from}</div>
                                  <div className="fw-semibold">To: {servicePeriod.to}</div>
                                </>
                              );
                            }
                            return <div className="fw-semibold">{servicePeriod.legacy}</div>;
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive mb-3">
                      <table className="table table-bordered invoice-table mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "60px" }}>#</th>
                            <th>Description</th>
                            <th style={{ width: "160px" }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>1</td>
                            <td>
                              <div className="fw-semibold">Service Charges</div>
                              <div className="text-muted small">
                                {categoryList.length > 0 ? categoryList.join(", ") : "Service Category"}
                              </div>
                              {selectedInvoice.credit_note ? (
                                <div className="text-muted small mt-1">Credit Note: {selectedInvoice.credit_note}</div>
                              ) : null}
                            </td>
                            <td className="fw-semibold">{formatAmount(selectedInvoice.invoice_amount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-6">
                        {selectedInvoice.document ? (
                          <div className="mt-1 no-print">
                            <a
                              className="btn btn-sm btn-outline-secondary"
                              href={`${apiurl}uploads/${selectedInvoice.document}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View Supporting Document
                            </a>
                          </div>
                        ) : null}
                      </div>
                      <div className="col-md-6">
                        <div className="invoice-total-card">
                          <div className="invoice-total-row">
                            <span>Subtotal</span>
                            <span>{formatAmount(selectedInvoice.invoice_amount)}</span>
                          </div>
                          <div className="invoice-total-row">
                            <span>Taxes</span>
                            <span>{formatAmount(0)}</span>
                          </div>
                          <div className="invoice-total-row total">
                            <span>Total Amount</span>
                            <span>{formatAmount(selectedInvoice.invoice_amount)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="invoice-footer-note">
                      This is a system-generated invoice and does not require a physical signature.
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <div className="d-flex gap-2 no-print">
                    <button className="btn btn-outline-primary" onClick={handlePrintInvoice}>
                      Print / Download
                    </button>
                    <button className="btn btn-outline-secondary" onClick={closeViewModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {showPaymentDetailsModal && selectedPaymentInvoice ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-semibold mb-1">Payment Details</h5>
                    <div className="text-muted small">
                      Invoice No:{" "}
                      <span className="fw-semibold">{selectedPaymentInvoice.invoice_no || "-"}</span>
                    </div>
                  </div>
                  <button className="btn-close" onClick={closePaymentDetailsModal}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Status</div>
                        <div className="fw-semibold">{getStatus(selectedPaymentInvoice).label}</div>
                        <div className="text-muted small mt-2">Invoice Date</div>
                        <div className="fw-semibold">{formatDate(selectedPaymentInvoice.invoice_date)}</div>
                        <div className="text-muted small mt-2">Created Time</div>
                        <div className="fw-semibold">{formatTime(selectedPaymentInvoice.created_at)}</div>
                        <div className="text-muted small mt-2">Business Associate</div>
                        <div className="fw-semibold">
                          {selectedPaymentInvoice.ba_name || "-"} ({selectedPaymentInvoice.ba_code || "-"})
                        </div>
                        <div className="text-muted small mt-2">Business Manager</div>
                        <div className="fw-semibold">{selectedPaymentInvoice.manager_name || "-"}</div>
                        <div className="text-muted small mt-2">Client</div>
                        <div className="fw-semibold">
                          {selectedPaymentInvoice.client_name || "-"} ({selectedPaymentInvoice.client_code || "-"})
                        </div>
                        <div className="text-muted small mt-2">Location</div>
                        <div className="fw-semibold">{selectedPaymentInvoice.location || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted small">Invoice Amount</div>
                        <div className="fw-semibold">{formatAmount(selectedPaymentInvoice.invoice_amount)}</div>
                        <div className="text-muted small mt-2">Sharing Ratio</div>
                        <div className="fw-semibold">{formatSharingRatio(selectedPaymentInvoice)}</div>
                        <div className="text-muted small mt-2">Payable Amount</div>
                        <div className="fw-semibold">
                          {(() => {
                            const payable = getPayableAmount(selectedPaymentInvoice);
                            return payable === null ? "-" : formatAmount(payable);
                          })()}
                        </div>
                        <div className="text-muted small mt-2">TDS Percentage</div>
                        <div className="fw-semibold">
                          {selectedPaymentInvoice.tds_percentage
                            ? `${selectedPaymentInvoice.tds_percentage}%`
                            : "-"}
                        </div>
                        <div className="text-muted small mt-2">Net Payable Amount</div>
                        <div className="fw-semibold">
                          {(() => {
                            const net = getNetPayableAmount(selectedPaymentInvoice);
                            return net === null ? "-" : formatAmount(net);
                          })()}
                        </div>
                        <div className="text-muted small mt-2">Bank Details</div>
                        <div className="fw-semibold">{formatBankDetails(selectedPaymentInvoice)}</div>
                        <div className="text-muted small mt-2">Service Categories</div>
                        <div className="fw-semibold">
                          {paymentCategoryList.length > 0 ? paymentCategoryList.join(", ") : "-"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <div className="text-muted small mb-1">Supporting Document</div>
                      {selectedPaymentInvoice.document ? (
                        <a
                          className="btn btn-sm btn-outline-secondary"
                          href={`${apiurl}uploads/${selectedPaymentInvoice.document}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View Supporting Document
                        </a>
                      ) : (
                        <div className="fw-semibold">Not uploaded</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted small mb-1">Payment Receipt</div>
                      {selectedPaymentInvoice.receipt_document ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success"
                          onClick={() =>
                            openPaymentReceiptDocument(selectedPaymentInvoice.receipt_document)
                          }
                        >
                          View Payment Receipt
                        </button>
                      ) : (
                        <div className="fw-semibold">Not uploaded</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={closePaymentDetailsModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {showValidationModal ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-semibold ">Manage Validation</h5>
                  </div>
                  <button className="btn-close" onClick={closeValidationModal}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-2 align-items-end mb-3">
                    <div className="col-md-7">
                      <label className="form-label small text-muted">Validation Parameter</label>
                      <input
                        className="form-control"
                        value={validationInput}
                        onChange={(e) => setValidationInput(e.target.value)}
                        placeholder="e.g. Supporting documents attached and verified"
                      />
                    </div>
                    <div className="col-md-2 d-grid">
                      <button className="btn btn-primary" onClick={handleValidationSave}>
                        {editingValidationId !== null ? "Update" : "Add"}
                      </button>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small text-muted">Filter</label>
                      <select
                        className="form-select"
                        value={validationFilter}
                        onChange={(e) => handleValidationFilterChange(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="active">Active Only</option>
                      </select>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered mb-0 align-middle">
                      <thead className="table-head-sky">
                        <tr>
                          <th style={{ width: "60px" }}>S.No</th>
                          <th>Parameter</th>
                          <th style={{ width: "110px" }}>Active</th>
                          <th style={{ width: "140px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationLoading ? (
                          <tr>
                            <td colSpan="4" className="text-center text-muted py-4">Loading...</td>
                          </tr>
                        ) : validationParams.length > 0 ? (
                          validationParams.map((item, index) => (
                            <tr key={`${item.id}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{item.parameter}</td>
                              <td>
                                <button
                                  className={`btn btn-sm ${Number(item.is_active) === 1 ? "btn-success" : "btn-outline-secondary"}`}
                                  onClick={() => handleValidationToggle(item)}
                                >
                                  {Number(item.is_active) === 1 ? "Active" : "Inactive"}
                                </button>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button className="btn btn-sm btn-outline-primary" onClick={() => handleValidationEdit(item)}>
                                    Edit
                                  </button>
                                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleValidationDelete(item)}>
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center text-muted py-4">No validation parameters</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={closeValidationModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {showCategoryModal ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-semibold">Manage Category</h5>
                  </div>
                  <button className="btn-close" onClick={closeCategoryModal}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Category <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                        placeholder="Category name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Sharing Ratio (%) <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        className="form-control"
                        value={categoryRatio}
                        onChange={(e) => setCategoryRatio(e.target.value)}
                        placeholder="e.g. 50"
                      />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={categoryDescription}
                        onChange={(e) => setCategoryDescription(e.target.value)}
                        placeholder="Short description"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Sharing Ratio Postfix</label>
                      <input
                        type="text"
                        className="form-control"
                        value={categoryPostfix}
                        onChange={(e) => setCategoryPostfix(e.target.value)}
                        placeholder="e.g. BA Share"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Status</label>
                      <Select
                        classNamePrefix="react-select"
                        options={categoryStatusOptions}
                        value={categoryStatusOptions.find((opt) => opt.value === categoryIsActive) || null}
                        onChange={(option) => setCategoryIsActive(option ? option.value : "1")}
                        isSearchable={false}
                        placeholder="Select Status"
                      />
                    </div>
                    <div className="col-md-9 d-flex justify-content-end align-items-end gap-2">
                      {editingCategoryId ? (
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setEditingCategoryId(null);
                            setCategoryName("");
                            setCategoryDescription("");
                            setCategoryRatio("");
                            setCategoryPostfix("");
                            setCategoryIsActive("1");
                          }}
                        >
                          Cancel Edit
                        </button>
                      ) : null}
                      <button className="btn btn-primary" onClick={handleCategorySave}>
                        {editingCategoryId ? "Update Category" : "Add Category"}
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered align-middle mb-0">
                      <thead className="table-head-sky">
                        <tr>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Sharing Ratio</th>
                          <th style={{ width: "110px" }}>Status</th>
                          <th style={{ width: "180px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryLoading ? (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-3">Loading...</td>
                          </tr>
                        ) : categoryParams.length > 0 ? (
                          categoryParams.map((item) => (
                            <tr key={item.id}>
                              <td>{item.category}</td>
                              <td>{item.description || "-"}</td>
                              <td>
                                {item.sharing_ratio}
                                {item.sharing_postfix ? ` ${item.sharing_postfix}` : ""}
                              </td>
                              <td>
                                <button
                                  className={`btn btn-sm ${Number(item.is_active) === 1 ? "btn-success" : "btn-outline-secondary"}`}
                                  onClick={() => handleCategoryToggle(item)}
                                >
                                  {Number(item.is_active) === 1 ? "Active" : "Inactive"}
                                </button>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleCategoryEdit(item)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleCategoryDelete(item.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-3">No categories found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={closeCategoryModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {showAddVendorModal ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title fw-semibold">Add Vendor</h5>
                  <button className="btn-close" onClick={closeAddVendorModal}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Client / Vendor Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newVendorName}
                        onChange={(e) => setNewVendorName(e.target.value)}
                        placeholder="Enter client or vendor name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newVendorLocation}
                        onChange={(e) => setNewVendorLocation(e.target.value)}
                        placeholder="Enter location"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={closeAddVendorModal}>
                    Close
                  </button>
                  <button className="btn btn-primary" onClick={handleAddVendorUiSubmit} disabled={addVendorLoading}>
                    {addVendorLoading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      <CreateInvoiceModal
        show={showModal}
        onClose={closeInvoiceModal}
        onSuccess={fetchInvoices}
        mode={editingInvoice ? "edit" : "create"}
        initialData={editingInvoice}
      />
    </>
  );
}

export default VendorPayment;
